import React from 'react';
import PropTypes from 'prop-types';
import {Map} from 'immutable';
import classnames from 'classnames';
import PureComponent from 'lib/purecomponent';
import {callTargetedAction} from 'client/framework';
import {GuideLevelModelV2} from 'resources/augmented/GuideLevel/GuideLevelModel.v2';
import {humanReadableDurationString} from 'lib/timeUtils.js';
import {stats} from 'lib/StatsUtil';
import Tooltip from 'generic/FixedTooltip/FixedTooltip.react';
import createLocalStore from 'lib/decorators/createLocalStore';
import makeRef from 'lib/makeRef';
import guideLevelListActions from './GuideLevelListActions';
import MasteryFlag from 'client/generic/MasteryFlag/MasteryFlag.react';

@createLocalStore(new Map({
  isNameOverflowing: false
}))
export default class LineItem extends PureComponent {
  static propTypes = {
    guideLevel: PropTypes.instanceOf(GuideLevelModelV2).isRequired,
    indentLevel: PropTypes.number.isRequired,
    isExpandable: PropTypes.bool,
    isExpanded: PropTypes.bool,
    expandStoreName: PropTypes.string,
    className: PropTypes.string
  };

  static defaultProps = {
    isExpandable: false,
    isExpanded: false
  };

  constructor(props) {
    super(props);
    this.makeRef = makeRef.bind(this);
    this.refName = `guide-${this.props.guideLevel.getId()}-name-ref`;
  }

  componentDidMount() {
    const ref = this[this.refName];
    const refParent = ref.parentElement;
    const isOverflowing = ref.clientWidth > refParent.clientWidth;

    if (isOverflowing) {
      this.setIsNameOverflowing();
    }
  }

  setIsNameOverflowing = () => {
    this.props.setProperty('isNameOverflowing', true);
  };

  toggleGuideExpansion = () => {
    if (this.props.isExpanded) {
      callTargetedAction({
        name: guideLevelListActions.GUIDE_TABLE_GUIDE_MINIMIZED,
        targetStore: this.props.expandStoreName,
        payload: this.props.guideLevel.getId()
      });
    } else {
      callTargetedAction({
        name: guideLevelListActions.GUIDE_TABLE_GUIDE_EXPANDED,
        targetStore: this.props.expandStoreName,
        payload: this.props.guideLevel.getId()
      });
    }
  };

  getNameCellContents() {
    const name = (
      <div
        className='guide-table-row-cell__inline-block'
        ref={this.makeRef}
        data-ref-name={this.refName}
        key={`${this.props.guideLevel.getId()}-name`}
      >
        {this.props.guideLevel.getName()}
      </div>
    );

    let tooltip;
    if(this.props.store.get('isNameOverflowing')) {
      tooltip = (
        <Tooltip type='white'>
          {this.props.guideLevel.getName()}
        </Tooltip>
      );
    }

    if (this.props.isExpandable) {
      return [(
        <span
          key={`${this.props.guideLevel.getId()}-expander`}
          className={classnames('fa guide-table-row-cell__expander', {
            'fa-chevron-down': this.props.isExpanded,
            'fa-chevron-right': !this.props.isExpanded
          })}
          onClick={this.toggleGuideExpansion}
        />
      ), name, tooltip];
    }
    return [name, tooltip];
  }

  render() {
    const indentClass = `guide-table-cell-indent-${this.props.indentLevel}`;
    const masteryFlagHasStarted = this.props.guideLevel.getMetaStudentCountOfGuesses() !== 0;
    const accuracyClass = classnames('guide-table-row-cell', {
      'guide-table-row-cell--na': this.props.guideLevel.getMetaStudentCountOfGuesses() === 0
    });
    const metaStudentAccuracy = this.props.guideLevel.getMetaStudentAccuracy();
    const metaStudentAccuracyColumnValue = metaStudentAccuracy !== null && metaStudentAccuracy !== undefined ? `${stats.toFixed(metaStudentAccuracy)}%` : 'N/A';
    return (
      <li className={classnames('guide-table-row', this.props.className)}>
        <div className={`${indentClass} guide-table-row-cell guide-table-row-cell--bold guide-table-row-cell--large`}>
          {this.getNameCellContents()}
        </div>
        <div className='guide-table-row-cell'>
          {humanReadableDurationString(this.props.guideLevel.getMetaTimeSpentDuration())}
        </div>
        <div className='guide-table-row-cell guide-table-row-cell--medium'>
          {this.props.guideLevel.getMetaCountOfUniqueQuestionsAnswered()} of {this.props.guideLevel.getMetaCountOfQuestions()}
        </div>
        <div className='guide-table-row-cell'>
          {stats.percentage(
            this.props.guideLevel.getMetaCountOfUniqueQuestionsAnswered(),
            this.props.guideLevel.getMetaCountOfQuestions()
          )}% complete
        </div>
        <div className='guide-table-row-cell'>
          {metaStudentAccuracyColumnValue}
        </div>
        <div className={accuracyClass}>
          <MasteryFlag
            accuracy={this.props.guideLevel.getMetaStudentAccuracy()}
            hasStarted={masteryFlagHasStarted}
          />
        </div>
      </li>
    );
  }
}
